import type { Todo } from '@/api'
import { currentLocale } from '@/i18n'

// Backend temporal values: "YYYY-MM-DD" (all-day) or an ISO datetime, with an offset or floating.

export function isAllDay(value: string | null): boolean {
  return !!value && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

// Milliseconds for sorting/comparisons. All-day and floating values count as local time; an
// all-day due date counts as the *end* of that day.
export function temporalTime(value: string | null, endOfDay = false): number | null {
  if (!value) return null
  if (isAllDay(value)) {
    const [y, m, d] = value.split('-').map(Number)
    return endOfDay
      ? new Date(y, m - 1, d, 23, 59, 59, 999).getTime()
      : new Date(y, m - 1, d).getTime()
  }
  const time = Date.parse(value)
  return Number.isNaN(time) ? null : time
}

export function dueTime(todo: Todo): number | null {
  return temporalTime(todo.due, true)
}

export function isOverdue(todo: Todo, now: Date = new Date()): boolean {
  const due = dueTime(todo)
  return (
    due !== null &&
    due < now.getTime() &&
    todo.status !== 'COMPLETED' &&
    todo.status !== 'CANCELLED'
  )
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

// "Today", "Tomorrow 14:00", "Mon 29 Sep", "29 Sep 2027" -- short enough for a list row chip.
export function formatTemporalShort(value: string | null, now: Date = new Date()): string {
  const time = temporalTime(value)
  if (time === null || !value) return ''
  const date = new Date(time)
  const dayDiff = Math.round((startOfDay(date) - startOfDay(now)) / 86_400_000)
  const locale = currentLocale()
  let day: string
  if (dayDiff >= -1 && dayDiff <= 1) {
    // "Today"/"Tomorrow"/"Yesterday" in the UI language ("today" -> "Today" in English).
    const word = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(dayDiff, 'day')
    day = word.charAt(0).toLocaleUpperCase(locale) + word.slice(1)
  } else if (Math.abs(dayDiff) < 7) day = date.toLocaleDateString(locale, { weekday: 'short' })
  else if (date.getFullYear() === now.getFullYear())
    day = date.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
  else day = date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
  if (isAllDay(value)) return day
  return `${day} ${date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`
}

export function formatTemporalLong(value: string | null): string {
  const time = temporalTime(value)
  if (time === null || !value) return ''
  const date = new Date(time)
  return isAllDay(value)
    ? date.toLocaleDateString(currentLocale(), { dateStyle: 'full' })
    : date.toLocaleString(currentLocale(), { dateStyle: 'full', timeStyle: 'short' })
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

// <input type="date"> / <input type="time"> values <-> backend strings.
export function toDateInput(value: string | null): string {
  const time = temporalTime(value)
  if (time === null) return ''
  const d = new Date(time)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function toTimeInput(value: string | null): string {
  if (!value || isAllDay(value)) return ''
  const time = temporalTime(value)
  if (time === null) return ''
  const d = new Date(time)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Local date + optional local time -> "YYYY-MM-DD" or an ISO datetime with the local offset.
export function fromInputs(date: string, time: string): string | null {
  if (!date) return null
  if (!time) return date
  const [y, m, d] = date.split('-').map(Number)
  const [hh, mm] = time.split(':').map(Number)
  const local = new Date(y, m - 1, d, hh, mm)
  const offsetMinutes = -local.getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  return `${date}T${pad(hh)}:${pad(mm)}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}

// A timestamp from the API (last used, ...) as a short local date and time; '' when unset or invalid.
export function formatDateTime(value: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(currentLocale(), { dateStyle: 'medium', timeStyle: 'short' })
}
